// File: src/components/home/ValueProposition.js

import React from 'react';
import styles from './ValueProposition.module.css';
import ScrollAnimation from '../common/ScrollAnimation';
import { cn } from '../../utils/helpers';

import { TbSatellite } from 'react-icons/tb';
import { PiCityLight } from 'react-icons/pi';
import { HiArrowTrendingUp } from 'react-icons/hi2';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import { MdOutlineBalance } from 'react-icons/md';
import { GiEarthWorm } from 'react-icons/gi';

const VALUE_PROPS = [
  {
    id: 'telemetry',
    eyebrow: 'Core Technology',
    title: 'Proprietary Wireless Telemetry',
    description:
      'At the heart of our operations is a unique, web-enabled telemetry system developed in collaboration with Toronto Metropolitan University (TMU) and based on NASA nano-satellite technology. This system monitors nine key scientific criteria in real-time, allowing for 24-hour remote management without on-site personnel — significantly reducing labour costs and ensuring optimal machine efficiency.',
    icon: <TbSatellite />,
    image: '/images/telemetry.png',
    stats: [
      { value: '9', label: 'Real-time criteria monitored' },
      { value: '24/7', label: 'Remote management' },
    ],
  },
  {
    id: 'urban',
    eyebrow: 'Urban Deployment',
    title: 'Urban-Friendly & Decentralized',
    description:
      'Unlike traditional composting or anaerobic digestion facilities located far from urban centres due to odour and noise concerns, the GS Digester is odourless, noiseless, and pollution-free. This enables a decentralized approach — small-footprint facilities located near the waste source, eliminating the high financial and environmental costs of transporting heavy, water-saturated organic waste.',
    icon: <PiCityLight />,
    image: '/images/urban.png',
    stats: [
      { value: '0', label: 'Odour emissions' },
      { value: '0', label: 'Noise pollution' },
    ],
  },
  {
    id: 'scalable',
    eyebrow: 'Scalable Deployment',
    title: 'Scalability',
    description:
      'The technology is fully scalable from three tonnes per day and upward. This scalability allows GreenScience to deploy solutions ranging from mid-sized facilities to large-scale regional operations, supporting growing processing capacity as demand increases.',
    icon: <HiArrowTrendingUp />,
    image: '/images/scalable.png',
    stats: [
      { value: '3+ Tonnes', label: 'Per day, minimum capacity' },
      { value: '∞', label: 'Scalable upward' },
    ],
  },
  {
    id: 'revenue',
    eyebrow: 'Business Model',
    title: 'Triple Revenue Stream',
    description:
      'While many competitors rely solely on tipping fees or product sales, GreenScience utilizes a three-pronged revenue model: collection fees for organic waste, the sale of high-value worm castings, and the sale of carbon offsets — receiving one tonne of carbon credit for every tonne of organic matter processed. ',
    disclaimer:
      'Disclaimer: The CO₂e claim assumes a 1:1 ratio, subject to jurisdiction and current regulatory practices.',
    icon: <RiMoneyDollarCircleLine />,
    image: '/images/revenue.png',
    stats: [
      { value: '3×', label: 'Revenue streams' },
      { value: '1:1', label: 'Carbon credits per tonne' },
    ],
  },
  {
    id: 'resilience',
    eyebrow: 'Market Position',
    title: 'Economic Resilience',
    description:
      'The model is not dependent on government subsidies or changes in consumer habits. It provides a cost-effective alternative to landfilling, which is increasingly being restricted by new legislation and rising tipping fees across North America.',
    icon: <MdOutlineBalance />,
    image: '/images/resilience.png',
    stats: [
      { value: '$0', label: 'Subsidy dependency' },
      { value: '↑', label: 'Tipping fees rising across NA' },
    ],
  },
  {
    id: 'product',
    eyebrow: 'End Product',
    title: 'Superior End Product',
    description:
      'GreenScience produces worm castings — often referred to as the "Cadillac of compost". Unlike chemical fertilizers or animal manure, these castings contain nutrients encased in a mucus membrane for natural time-release, preventing nutrient leaching and significantly improving crop yields and soil health.',
    icon: <GiEarthWorm />,
    image: '/images/product.png',
    stats: [
      { value: '↑', label: 'Crop yield improvement' },
      { value: '0', label: 'Chemical additives' },
    ],
  },
];

function ValueProposition() {
  return (
    <section className={styles.section}>

      {/* ── Section intro ── */}
      <div className={cn('site-container', styles.introWrap)}>
        <ScrollAnimation>
          <div className={styles.intro}>
            <p className={styles.eyebrow}>Why GreenScience?</p>
            <h2 className={styles.introHeading}>
              Six reasons our technology leads the industry.
            </h2>
          </div>
        </ScrollAnimation>
      </div>

      {/* ── Props ── */}
      <div className="site-container">
        {VALUE_PROPS.map((prop, i) => {
          const isEven = i % 2 === 0;
          return (
            <div key={prop.id} className={styles.propRow}>

              {/* Text side */}
              <ScrollAnimation
                className={cn(
                  styles.textCol,
                  !isEven && styles.textColRight
                )}
              >
                <p className={styles.propEyebrow}>{prop.eyebrow}</p>
                <div className={styles.propTitleRow}>
                  <span className={styles.propIcon}>{prop.icon}</span>
                  <h3 className={styles.propTitle}>{prop.title}</h3>
                </div>
                <p className={styles.propDesc}>{prop.description}</p>

                {/* Disclaimer — only rendered when the prop defines one */}
                {prop.disclaimer && (
                  <p className={styles.propDisclaimer}>{prop.disclaimer}</p>
                )}

                {/* Stat pills */}
                <div className={styles.stats}>
                  {prop.stats.map((s) => (
                    <div key={s.label} className={styles.stat}>
                      <span className={styles.statValue}>{s.value}</span>
                      <span className={styles.statLabel}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </ScrollAnimation>

              {/* Visual side */}
              <ScrollAnimation
                className={cn(
                  styles.visualCol,
                  !isEven && styles.visualColLeft
                )}
                delay={100}
              >
                <div className={styles.visualCard}>
                  <img
                    src={prop.image}
                    alt={prop.title}
                    className={styles.visualImg}
                  />
                  <div className={styles.visualOverlay} />
                  <span className={styles.visualIndex}>0{i + 1}</span>
                </div>
              </ScrollAnimation>

            </div>
          );
        })}
      </div>

    </section>
  );
}

export default ValueProposition;